import React, { useId } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useStyles } from './styles';
import { LoadingButton } from '@mui/lab';

export default function AlertDialog({
  open,
  handleClose,
  title,
  description,
  btnText,
  dialogProps,
  helperText,
  onPrimaryButtonClick,
  primaryButtonProps,
  children,
  modal,
  isButtonLoading,
  btnAutoFocus = false,
}) {
  const classes = useStyles();
  const { t } = useTranslation();
  const dialogTitleId = useId();
  const dialogDescriptionId = useId();
  return (
    <Dialog
      open={open}
      onClose={handleClose}
      aria-labelledby={dialogTitleId}
      aria-describedby={dialogDescriptionId}
      PaperProps={{ className: clsx({ [classes.modal]: modal }) }}
      {...dialogProps}
    >
      <DialogTitle id={dialogTitleId} variant="h3" component="h2">
        {title}
      </DialogTitle>
      {modal && (
        <IconButton
          aria-label={t('close', 'Close')}
          data-testid="close-icon"
          onClick={handleClose}
          sx={{ position: 'absolute', right: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>
      )}
      {helperText && <DialogContentText sx={{ px: 3 }}> {helperText} </DialogContentText>}
      <DialogContent sx={{ overflow: 'initial' }}>
        {children ? (
          children
        ) : (
          <DialogContentText id={dialogDescriptionId}>{description}</DialogContentText>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} color="primary" autoFocus={btnAutoFocus}>
          {btnText}
        </Button>
        {primaryButtonProps.title && (
          <LoadingButton
            loading={isButtonLoading}
            onClick={onPrimaryButtonClick}
            disabled={primaryButtonProps.isDisabled || isButtonLoading}
            variant="contained"
          >
            {primaryButtonProps.title}
          </LoadingButton>
        )}
      </DialogActions>
    </Dialog>
  );
}

AlertDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
  title: PropTypes.string.isRequired,
  description: PropTypes.string,
  btnText: PropTypes.string.isRequired,
  showCloseIcon: PropTypes.bool,
  onPrimaryButtonClick: PropTypes.func,
  modal: PropTypes.bool,
  isButtonLoading: PropTypes.bool,
  btnAutoFocus: PropTypes.bool,
};

AlertDialog.defaultProps = {
  primaryButtonProps: {
    isDisabled: false,
    title: '',
  },
  modal: false,
  isButtonLoading: false,
  btnAutoFocus: true,
  dialogProps: {},
};
